import { OCSController, Application } from '@ocs/controller'
import { renderTwig } from '@ocs/template-manager'
import HeadingTemplate from './static/html/heading_template.html.twig' with { type: 'text' }
import ProductTemplate from './static/html/product_template.html.twig' with { type: 'text' }
import NoProductsTemplate from './static/html/no_products_template.html.twig' with { type: 'text' }

const WFO_NAMES = {
    OUN: 'NWS Norman, OK',
    WNS: 'Storm Prediction Center',
    TSA: 'NWS Tulsa, OK',
    SHV: 'NWS Shreveport, LA',
    AMA: 'NWS Amarillo, TX',
}
const DEFAULT_WFO_NAME = 'National Weather Service'

export class OCSTextProductsController extends OCSController {
    static targets = ['heading', 'loading', 'list', 'error']
    initialize() {
        super.initialize()
        this.dataTypes = {
            'booleanOptions.*': 'boolean',
            'booleanOptions.*.*': 'boolean',
            stid: 'string',
        }
        this.boundMethods.onDOMChange = this.onDOMChange.bind(this)
        this.boundMethods.requestData = this.requestData.bind(this)
        this.currentData = null
        this.refreshProcess = null
        this.advisories = null
        this.advisoriesRequest = null
    }

    connect() {
        super.connect()
        this.load()

        if (document.body.dataset.mesoStationId) {
            this.options.ajaxOptions.stid = document.body.dataset.mesoStationId
            this.startRefreshingData()
        } else {
            const observer = new MutationObserver((mutations) => {
                for (const mutation of mutations) {
                    if (
                        mutation.type === 'attributes' &&
                        mutation.attributeName === 'data-meso-station-id'
                    ) {
                        observer.disconnect()
                        this.options.ajaxOptions.stid =
                            document.body.dataset.mesoStationId
                        this.startRefreshingData()
                        if (this.currentData && this.currentData.length) {
                            this.onRequestSuccess(this.currentData)
                        }
                    }
                }
            })
            observer.observe(document.body, { attributes: true })
        }
        this.element.addEventListener(
            'stationChanged',
            this.handleStationChange.bind(this),
        )
        document.addEventListener(
            'stationChanged',
            this.handleStationChange.bind(this),
        )
    }

    disconnect() {
        super.disconnect()
        this.stopRefreshingData()
        this.unload()
    }

    load() {
        this.renderProducts()
    }

    unload() {
        this.element.innerText = ''
    }

    /**
     * Methods
     */

    async refreshData() {
        const data = await this.requestData()
        return data
    }

    async requestData() {
        const apiHost = this.options.ajaxOptions.apiHost || 'api.mesonet.org'
        const stid = this.options.ajaxOptions.stid
        const url = `https://${apiHost}/v2/products/nws/text-products?stid=${stid}`
        const response = await fetch(url)
        const json = await response.json()
        await this.loadAdvisories()
        this.onRequestSuccess(json)
        return json
    }

    loadAdvisories() {
        if (!this.advisoriesRequest) {
            const url = this.options.ajaxOptions.advisoriesUrl
            this.advisoriesRequest = fetch(url)
                .then((response) => response.json())
                .then((config) => {
                    this.advisories = this.buildAdvisoryLookup(config)
                })
                .catch(() => {
                    this.advisoriesRequest = null
                })
        }
        return this.advisoriesRequest
    }

    buildAdvisoryLookup(config) {
        const byCode = {}
        for (const [pCode, phenomenon] of Object.entries(config.phenomena)) {
            for (const [sCode, significance] of Object.entries(
                config.significance,
            )) {
                const key = `${pCode}.${sCode}`
                byCode[key] = {
                    name:
                        config.overrides?.[key]?.en ||
                        `${phenomenon.en} ${significance.en}`,
                    color: config.colors?.[key]?.hex,
                    sort: [
                        config.sort.significance[sCode] ?? Infinity,
                        config.sort.phenomena[pCode] ?? Infinity,
                    ],
                }
            }
        }
        return byCode
    }

    prepareProducts(products) {
        if (!this.advisories) {
            return products
        }
        const lookup = (product) =>
            this.advisories[`${product.phenomenon}.${product.significance}`]
        return products
            .map((product) => {
                const advisory = lookup(product)
                if (!advisory) return product
                return {
                    ...product,
                    headline: advisory.name,
                    nwsProductColor: advisory.color && `#${advisory.color}`,
                }
            })
            .sort((a, b) => {
                const [aSig, aPhen] = lookup(a)?.sort || [Infinity, Infinity]
                const [bSig, bPhen] = lookup(b)?.sort || [Infinity, Infinity]
                if (aSig === bSig && aPhen === bPhen) return 0
                return aSig - bSig || aPhen - bPhen || 0
            })
    }

    formatEndTime(isoTime) {
        if (!isoTime) return ''
        return new Date(isoTime).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            timeZoneName: 'short',
        })
    }

    renderProducts(products = []) {
        products = this.prepareProducts(products).map((product) => ({
            ...product,
            endTimeText: this.formatEndTime(product.endTime),
            wfoName: WFO_NAMES[product.wfo] || DEFAULT_WFO_NAME,
        }))
        const ops = this.options
        const renderedElements = []
        if (
            ops.options.heading.enabled &&
            (products.length || !ops.booleanOptions.hideIfNoActiveProducts)
        ) {
            const headingHTML = renderTwig(HeadingTemplate, ops.options.heading)
            renderedElements.push(headingHTML)
        }
        if (products.length) {
            products.forEach((product) => {
                const productTemplateData = {
                    options: ops.options.product,
                    data: product,
                    dataHost: this.options.ajaxOptions.dataHost || 'data.mesonet.org',
                }
                const productHTML = renderTwig(
                    ProductTemplate,
                    productTemplateData,
                )
                renderedElements.push(productHTML)
            })
        } else if (!ops.booleanOptions.hideIfNoActiveProducts) {
            const messageHTML = renderTwig(
                NoProductsTemplate,
                this.currentData ? ops.options.noProducts : ops.options.loading,
            )
            renderedElements.push(messageHTML)
        }

        this.element.innerText = ''
        renderedElements.forEach((html) => {
            this.element.appendChild(
                document.createRange().createContextualFragment(html),
            )
        })
    }

    async startRefreshingData() {
        await this.refreshData()
        const currentOptions = this.options.options
        if (!this.refreshProcess && currentOptions.refreshRate) {
            const rate = currentOptions.refreshRate * 1000
            this.refreshProcess = setInterval(this.refreshData.bind(this), rate)
        }
    }

    stopRefreshingData() {
        if (this.refreshProcess) {
            clearInterval(this.refreshProcess)
            this.refreshProcess = null
        }
    }

    /**
     * Event listeners
     */

    /**
     * Respond to changes in the options stored in the DOM
     *
     * @param {Object} inMutations
     * @param {Object} inObserver
     */
    onDOMChange(inMutations, inObserver) {
        super.onDOMChange(inMutations, inObserver)
        let attributesChange = false
        for (const mutation of inMutations) {
            if (mutation.type === 'attributes') {
                attributesChange = true
            }
        }
        if (attributesChange) {
            this.refreshData()
        }
    }

    onRequestSuccess(inJsonData) {
        return new Promise((resolve, reject) => {
            if (inJsonData.error) {
                reject(inJsonData.error)
            }
            if (inJsonData && inJsonData.data) {
                this.currentData = inJsonData
                this.renderProducts(this.currentData.data.products)
                resolve()
            } else {
                const err = new Error()
                err.message = 'No data in response.'
                err.name = 'No data error'
                reject(err)
            }
        })
    }

    handleStationChange(event) {
        const newStid = event.detail.stid || document.body.dataset.mesoStationId
        if (newStid) {
            this.options.ajaxOptions.stid = newStid
            this.refreshData()
        }
    }

    get currentData() {
        return this._data
    }

    set currentData(inData) {
        this._data = inData
    }

    get defaultOptions() {
        return {
            ajaxOptions: {
                apiHost: document.body.dataset.apiHost || 'api.mesonet.org',
                url: 'https://{apiHost}/v2//products/nws/text-products?stid={stid}',
                advisoriesUrl:
                    'https://content.mesonet.org/repos/config-interactive-products/common/nws/nws-advisories.json',
            },
            booleanOptions: {
                hideIfNoActiveProducts: false,
            },
            cssClasses: {},
            options: {
                refreshRate: 120,
                heading: {
                    enabled: true,
                    tag: 'h2',
                    class: '',
                    text: 'Current Watches and Warnings',
                },
                product: {
                    container: {
                        class: 'd-flex w-100 align-items-center mb-2',
                    },
                    headline: {
                        tag: 'div',
                        class: 'fw-bold',
                    },
                    expiration: {
                        tag: 'div',
                        class: 'fw-normal small text-muted',
                    },
                    issuingOffice: {
                        tag: 'div',
                        class: 'fw-normal small text-muted',
                    },
                    productColor: {
                        enabled: true,
                        class: 'border rounded-3 me-2 bg-gradient flex-shrink-0',
                        width: '40px',
                        height: '40px',
                    },
                },
                loading: {
                    class: 'small text-muted text-center py-5',
                    text: 'Loading&hellip;',
                },
                noProducts: {
                    class: 'small text-muted text-center py-5',
                    text: 'No watches or warnings in effect',
                },
            },
        }
    }
}

Application.start().register('textproducts', OCSTextProductsController)
